import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import axios from 'axios';

import { API } from '@/utils/api';

/**
 * ApisAI System Footer - displayed on all PUBLIC pages only
 * Purpose: Viral growth, branding, referral tracking
 * 
 * NOT for panel/dashboard pages - only for:
 * - PublicKennel
 * - PublicLitter
 * - PublicDog
 * - PublicStud
 * - KennelsByLocation
 * - LittersByLocation
 */
export default function ApisAIFooter({ variant = 'default', pageType = 'unknown', pageSlug = '', theme = null }) {
  const [logoError, setLogoError] = useState(false);
  
  // Extract page info from URL if not provided
  const getPageInfo = () => {
    const path = window.location.pathname;
    let type = pageType;
    let slug = pageSlug;
    
    if (type === 'unknown') {
      if (path.includes('/hodowla/')) {
        type = 'kennel';
        slug = path.split('/hodowla/')[1]?.split('/')[0] || '';
      } else if (path.includes('/miot/')) {
        type = 'litter';
        slug = path.split('/miot/')[1]?.split('/')[0] || '';
      } else if (path.includes('/pies/')) {
        type = 'dog';
        slug = path.split('/pies/')[1]?.split('/')[0] || '';
      } else if (path.includes('/reproduktor/')) {
        type = 'stud';
        slug = path.split('/reproduktor/')[1]?.split('/')[0] || '';
      }
    }
    
    return { type, slug };
  };

  const trackFooterClick = async (clickTarget = 'cta') => {
    try {
      const { type, slug } = getPageInfo();
      
      await axios.post(`${API}/track/footer`, {
        event_type: 'footer_click_referral',
        click_target: clickTarget, // 'cta' or 'logo'
        page_type: type,
        page_slug: slug,
        source_url: window.location.href,
        referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      // Silent fail - don't block user experience
      console.log('Footer tracking error:', e);
    }
  };

  // Common CTA URL - production ApisAI landing
  const ctaUrl = 'https://apisai.pl';

  // Logo with fallback
  const LogoWithFallback = ({ className = '', textClassName = '' }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      {!logoError ? (
        <img 
          src="/logo.svg" 
          alt="ApisAI" 
          className="w-8 h-8"
          onError={() => setLogoError(true)}
        />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-[#F47A3B] flex items-center justify-center">
          <span className="text-white font-bold text-lg">A</span>
        </div>
      )}
      <span className={`font-semibold ${textClassName}`}>APISAI</span>
    </div>
  );

  // Light variant for dark backgrounds (PublicDog, PublicStud, location pages)
  if (variant === 'light') {
    return (
      <footer
        className="border-t border-white/10 bg-[#0B1F3B]/80 backdrop-blur-sm"
        data-testid="apisai-system-footer"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Mobile: Stack layout / Desktop: Row layout */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Logo - clickable */}
            <a 
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFooterClick('logo')}
              className="hover:opacity-80 transition-opacity flex-shrink-0"
              data-testid="footer-logo-link"
            >
              <LogoWithFallback textClassName="text-white" />
            </a>
            
            {/* Center text - hidden on very small screens */}
            <p className="text-white/70 text-xs sm:text-sm text-center flex-1 hidden xs:block">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 text-[#F47A3B]" />
              Strona w systemie <span className="text-white font-medium">ApisAI</span> — widoczność AI + więcej zapytań
            </p>
            
            {/* CTA Button */}
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFooterClick('cta')}
              className="flex items-center gap-2 px-4 py-2 bg-[#F47A3B] hover:bg-[#E06A2B] text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
              data-testid="footer-cta-button"
            >
              Sprawdź ApisAI
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </footer>
    );
  }

  // Default variant for all public pages - matches page theme when provided, else white
  return (
    <footer data-testid="apisai-system-footer">
      {/* Theme-matched (or white) bar */}
      <div className={`border-t ${theme ? theme.border : 'border-[rgba(17,24,39,0.10)]'} ${theme ? theme.bg : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Mobile: Stack layout / Desktop: Row layout */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Logo - clickable */}
            <a 
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFooterClick('logo')}
              className="hover:opacity-80 transition-opacity flex-shrink-0"
              data-testid="footer-logo-link"
            >
              <LogoWithFallback textClassName={theme ? theme.text : 'text-[#0B1F3B]'} />
            </a>
            
            {/* Center text */}
            <p className={`${theme ? theme.textMuted : 'text-[var(--zinc-500,#71717a)]'} text-xs sm:text-sm text-center flex-1`}>
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 text-[#F47A3B]" />
              Strona w systemie <span className={`${theme ? theme.text : 'text-zinc-900'} font-medium`}>ApisAI</span> — widoczność AI + więcej zapytań
            </p>
            
            {/* CTA Button */}
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFooterClick('cta')}
              className="flex items-center gap-2 px-4 py-2 bg-[#F47A3B] hover:bg-[#E06A2B] text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
              data-testid="footer-cta-button"
            >
              Sprawdź ApisAI
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
